import React from 'react';
import { Zap } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { cn } from '../../utils/cn';
import { CHIP_DENSE } from '../../utils/chipColors';

/**
 * Visual indicator that the platform's price-chase loop ran on a lifecycle
 * stage (entry / SL / exit). Renders as a small ⚡×N chip with a hover summary:
 *
 *   Chase ×2 · 588.30 → 655.40 · filled 635.10
 *
 * For in-flight chase (no fill yet) the summary swaps the trailing "filled X"
 * for "chasing now".
 *
 * Render this only when chase actually fired (caller decides — see
 * `hasChase` helper). The component itself does NOT render when count <= 0,
 * so it's safe to drop into a layout without per-leg gating.
 */
export type ChaseKind = 'entry' | 'sl' | 'exit';

export interface ChaseBadgeProps {
  kind: ChaseKind;
  /** Reprice count. <= 0 → component renders nothing. */
  count: number;
  /** Initial LIMIT placed by the platform (entry_price / sl_limit_price /
   *  expected_exit_price). Used as the LHS of `initial → final`. */
  initialLimit?: number | null;
  /** Final chase LIMIT (last_*_reprice_limit). Used as the RHS. */
  finalLimit?: number | null;
  /** Actual broker fill. undefined → chase is still in flight. */
  fillPrice?: number | null;
  className?: string;
}

const KIND_LABEL: Record<ChaseKind, string> = {
  entry: 'Entry chase',
  sl: 'SL chase',
  exit: 'Exit chase',
};

const fmt = (v: number | null | undefined): string =>
  v == null ? '—' : v.toFixed(2);

/**
 * Returns true when chase data on a position indicates the chase loop ran for
 * the given stage. Keep the detection in one place so callers stay terse.
 */
export const hasChase = (
  kind: ChaseKind,
  data: {
    entry_reprice_count?: number;
    entry_chase_started_at?: string | null;
    sl_reprice_count?: number;
    sl_chase_started_at?: string | null;
    exit_chase_started_at?: string | null;
    exit_attempt_count?: number;
  } | null | undefined,
): boolean => {
  if (!data) return false;
  if (kind === 'entry') {
    return (data.entry_reprice_count ?? 0) > 0
      || !!data.entry_chase_started_at;
  }
  if (kind === 'sl') {
    return (data.sl_reprice_count ?? 0) > 0
      || !!data.sl_chase_started_at;
  }
  // exit — position record doesn't carry a dedicated reprice count; the
  // start-timestamp is the reliable signal (set on the first chase tick).
  return !!data.exit_chase_started_at;
};

export const ChaseBadge: React.FC<ChaseBadgeProps> = ({
  kind,
  count,
  initialLimit,
  finalLimit,
  fillPrice,
  className,
}) => {
  if (count <= 0) return null;

  const inFlight = fillPrice == null;
  const reprices = `${count} reprice${count === 1 ? '' : 's'}`;
  const drift = (initialLimit != null && finalLimit != null)
    ? `${fmt(initialLimit)} → ${fmt(finalLimit)}`
    : null;

  const summary = [
    `${KIND_LABEL[kind]} ×${count}`,
    drift,
    inFlight ? 'chasing now' : `filled ${fmt(fillPrice)}`,
  ].filter(Boolean).join(' · ');

  // Tooltip wraps a plain non-button span. We use `inline-flex` so the chip
  // sits inline with neighbours (e.g. the StatusChip) without breaking the
  // header row.
  return (
    <Tooltip text={summary}>
      <span
        className={cn(
          'inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold',
          CHIP_DENSE.CHASE,
          inFlight && 'animate-pulse',
          className,
        )}
        aria-label={summary}
      >
        <Zap className="w-2.5 h-2.5" strokeWidth={2.5} />
        <span className="tabular-nums">×{count}</span>
        <span className="sr-only">{reprices}</span>
      </span>
    </Tooltip>
  );
};

export default ChaseBadge;
